import * as Sentry from '@sentry/browser';
import queryString from 'query-string';
import { useCookies } from 'react-cookie';

import { SaleCaseFileTypeValue } from '@type-def_iqfu/SaleCasesType';

import { UserType } from '@constants_common';
import { DealType, PurchaseType, SaleCasesSortKey, SaleCasesStatusType } from '@constants_iqfu';

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

export type SaleCase = {
  id: number;
  status: 'draft' | 'unapproved' | 'rejected' | 'approved'; // 承認ステータス。
  is_public: boolean;
  company_name: string; // 不動産会社名。未承認かつインフォメーションマップがない実績にはマップなしの文言をつけて返す。
  contract_date: string; // format: date, example: '2021-10-20', 売買契約年月日。
  deal_type: 'mediation' | 'purchase'; // 取引方法。
  purchase_type: 'other_company' | 'in_house'; // 買取タイプ。
  price: number; // 売買価格。単位は万円。
  property_type: 'mansion' | 'land' | 'used_house' | 'new_house'; // 物件種別。
  full_address: string; // 都道府県、市区町村も結合した住所を返す。マンションはマンション名も末尾に追加する。
  destroyable: boolean; // 削除が可能か不可能か。
  created_at: string;
};

export const useSaleCase = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getSaleCases = async (
    userType: UserType,
    queryParams: {
      offset: number;
      limit: number;
      company_id?: string;
      address?: string;
      mansion_name?: string;
      property_type?: string;
      status?: '' | keyof typeof SaleCasesStatusType;
      has_information_map?: '' | 'true' | 'false';
      created_date_from?: string;
      created_date_to?: string;
      contract_date_from?: string;
      contract_date_to?: string;
      deal_type?: '' | keyof typeof DealType;
      purchase_type?: '' | keyof typeof PurchaseType;
      sort_key?: '' | keyof typeof SaleCasesSortKey;
      sort_order?: '' | 'asc' | 'desc';
      is_public?: boolean | null;
    },
  ): Promise<{
    offset: number; // 取得データのoffset
    limit: number; // 取得データの件数
    total_count: number; // 取得条件のデータ総数
    sale_cases: SaleCase[];
  }> => {
    if (queryParams.offset < 0) queryParams.offset = 0;
    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/${userType}/sale_cases?${query}`;
    console.log('url', url);
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSaleReasons = async () => {
    let url = `${backendBasePath}/sale_reasons`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getSaleCaseDetail = async (sale_case_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/sale_cases/${sale_case_id}`;
    let status;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 404) throw new Error();
        status = 'success';
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        status = 'error';
      });
    return { status, result };
  };

  const createSaleCase = async (formData: FormData) => {
    const url = `${backendBasePath}/company/sale_cases`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        let plainObject: { [key: string]: FormDataEntryValue } = {};
        formData.forEach((value: FormDataEntryValue, key: string) => {
          plainObject[key] = value;
        });
        Sentry.withScope((scope) => {
          scope.setExtra('payload', plainObject);
          Sentry.captureException(error);
        });
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteSaleCase = async (sale_case_id: string, userType: UserType) => {
    const url = `${backendBasePath}/${userType}/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateSaleCase = async (sale_case_id: string, formData: FormData) => {
    const url = `${backendBasePath}/company/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        let plainObject: { [key: string]: FormDataEntryValue } = {};
        formData.forEach((value: FormDataEntryValue, key: string) => {
          plainObject[key] = value;
        });
        Sentry.withScope((scope) => {
          scope.setExtra('payload', plainObject);
          Sentry.captureException(error);
        });
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const approveSaleCase = async (
    sale_case_id: string,
    submitData: {
      is_public: boolean;
      status: 'draft' | 'unapproved' | 'rejected' | 'approved';
      rejected_reason: string;
      file_type: SaleCaseFileTypeValue;
    },
  ) => {
    const url = `${backendBasePath}/admin/sale_cases/${sale_case_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const uploadCsvFile = async (formData: FormData) => {
    const url = `${backendBasePath}/admin/sale_cases/import`;
    let status;
    const results = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: formData,
    })
      .then((response) => {
        if (response.status === 200) {
          status = 'success';
          return response.json();
        } else {
          status = response.status;
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return { status, results };
  };

  type ReformItemOption = {
    id: number;
    name: string;
    description: string;
    property_type: 'mansion' | 'used_house' | 'new_house';
  };
  const getReformItems = async (
    userType: UserType,
    property_type: 'mansion' | 'new_house' | 'used_house',
  ): Promise<{ reform_items: ReformItemOption[] }> => {
    const url = `${backendBasePath}/${userType}/reform_items?property_type=${property_type}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getSaleCases,
    getSaleCaseDetail,
    getSaleReasons,
    createSaleCase,
    updateSaleCase,
    deleteSaleCase,
    approveSaleCase,
    uploadCsvFile,
    getReformItems,
  };
};
